import {
  type AnimationEvent,
  Children,
  cloneElement,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from "react";
import { useReducedMotion } from "../lib/useReducedMotion";
import { releaseWillChange } from "./release-will-change";

/**
 * StaggerReveal, a wrapper that fades-in-up children with a tight
 * 40ms stagger.
 *
 * Validated against Apple Health Summary (~50ms stagger), Linear
 * (synchronous, no stagger), Stripe (synchronous), Vercel (synchronous).
 * 40ms is the modern "we have stagger but it doesn't read as
 * choreography" band; 60-80ms reads slightly theatrical and 100ms+
 * reads dated.
 *
 * Patient surface only. Ops surfaces (Stripe / Mercury / Vercel
 * pattern) render synchronously; choreography becomes information
 * that competes with the data.
 *
 * Caps at 6 staggered children. Beyond that, the rest render
 * synchronously - a long tail of staggered cards reads slow on a
 * grid scan.
 *
 * `prefers-reduced-motion: reduce`: snaps; no animation, no delays.
 *
 * Compositor-only: each child gets `animation: fade-in-up …` plus
 * `animation-delay: Nms` inline. No re-renders during the animation.
 * The CSS `--motion-stagger-tight` token is the source of truth.
 *
 * `will-change` is dropped when the reveal finishes. It is a hint that a
 * change is imminent, and WebKit answers it by giving the element its own
 * compositing layer: on retina that is width x height x 4 bytes x 4, held for
 * as long as the hint is there. Left on after a one-shot entrance it buys
 * nothing and costs that memory for the life of the page, once per child
 * across every StaggerReveal on screen. HeroReveal and the operations Stagger
 * already clear theirs the same way.
 */

export interface StaggerRevealProps {
  children: ReactNode;
  /**
   * Maximum number of children to stagger. Beyond this, children
   * render synchronously (no animation). Defaults to 6.
   */
  cap?: number;
  /**
   * Optional first-child delay, in ms. Useful when sequencing the
   * grid after a hero reveal has settled.
   */
  initialDelay?: number;
  /**
   * Gap between children, in ms. The 40ms default is the list rhythm
   * (cards in a grid). Page SECTIONS need ~100ms to read as
   * choreography, matching the snapshot page's 280ms/60-160ms system.
   */
  stepMs?: number;
  /**
   * When false, skips the stagger and renders children unwrapped - e.g.
   * after a view-transition morph already played the entrance. Defaults
   * to true.
   */
  active?: boolean;
}

const STAGGER_MS = 40;
const FADE_DURATION_MS = 280;

export function StaggerReveal({
  children,
  cap = 6,
  initialDelay = 0,
  stepMs = STAGGER_MS,
  active = true,
}: StaggerRevealProps) {
  const reduced = useReducedMotion();
  const items = Children.toArray(children);

  if (reduced || !active) {
    return <>{children}</>;
  }

  return (
    <>
      {items.map((child, index) => {
        if (!isValidElement(child)) return child;
        if (index >= cap) return child;

        const delayMs = initialDelay + index * stepMs;
        const childProps = (child.props as { style?: React.CSSProperties }) ?? {};
        const existingStyle = childProps.style ?? {};

        const merged: React.CSSProperties = {
          ...existingStyle,
          animation: `fade-in-up ${FADE_DURATION_MS}ms var(--ease-snap-spring) ${delayMs}ms both`,
          willChange: "opacity, transform",
        };

        const childOnAnimationEnd = (
          child.props as { onAnimationEnd?: (event: AnimationEvent<HTMLElement>) => void }
        ).onAnimationEnd;

        return cloneElement(
          child as ReactElement<{
            style?: React.CSSProperties;
            onAnimationEnd?: (event: AnimationEvent<HTMLElement>) => void;
          }>,
          {
            style: merged,
            onAnimationEnd: (event: AnimationEvent<HTMLElement>) => {
              releaseWillChange(event, "fade-in-up");
              childOnAnimationEnd?.(event);
            },
          },
        );
      })}
    </>
  );
}
